import React, { useEffect, useState } from "react";
import Flatpickr from "react-flatpickr";
import CustomPowerConsumptionChart from "./CustomPowerConsumptionChart";
import styles from "./MonitoringComponents.module.scss";
import axios from "axios";
import moment from "moment";
import { 
  Button,
  ButtonGroup,
  Label 
} from "reactstrap";
import { 
  areDatesOnSameDay,
  convertJsToUnixSeconds,
  convertUnixToJs,
  convertUnixToJsWithDateNoTime,
  getEndOfDay,
  getStartOfDay,
} from "./Helper";

function PowerConsumptionChart(props) {
  const { buildingId, userRole, contractCap } = props;

  // --- Data mode switch ---
  const [dataMode, setDataMode] = useState('live');
  const isLiveData = dataMode === 'live';

  // --- Date Picker ---
  const [startPicker, setStartPicker] = useState(getStartOfDay(new Date()));
  const [endPicker, setEndPicker] = useState(new Date());

  // --- Interval Picker ---
  const [intervalPicker, setIntervalPicker] = useState('30min');
  const intervals = ['30min', '1hour'];

  const [currentTime, setCurrentTime] = useState(moment());

  // --- Line Chart Data ---
  const [rawPowerData, setRawPowerData] = useState([]);
  const [lineChartData, setLineChartData] = useState([]);
  const [lineChartTotal, setLineChartTotal] = useState([]);
  const [dataProcessingStatus, setDataProcessingStatus] = useState('loading');
  
  // --- batMSB Data ---
  const [batMSBData, setBatMSBData] = useState([]);

  // Handles aggregation of raw data for historical data
  const getIntervalInSeconds = (interval) => {
    switch (interval) {
      case "30min": return 30 * 60;
      case "1hour": return 60 * 60;
      default: return 30 * 60;
    }
  }

  // Fetches batMSB data when buildingId changes
  useEffect(() => {
    let alive = true;

    const fetchBatMSBData = async () => {
      try {
        const response = await axios.get(`/table_names/building/${buildingId}`);
        if (alive && response.data) {
          setBatMSBData(response.data);
        }
      } catch (error) {
        console.error('Error fetching batMSB data:', error);
        setBatMSBData([]);
      }
    };

    if (buildingId) {
      fetchBatMSBData();
    }

    return () => {
      alive = false;
    };
  }, [buildingId]);

  // Fetches power data upon date change or every 30s
  useEffect(() => {
    let alive = true;

    const fetchPowerData = async (selectedValue, startTimestamp, endTimestamp) => {
      const endpoint = `getPowerData${userRole === "admin" ? "Admin" : ""}`; 

      const currentPower = await axios.get(`/assets/${endpoint}`, {
        params: {
          buildingId: selectedValue,
          startTime: startTimestamp,
          endTime: endTimestamp,
          interval: intervalPicker,
        }, 
      });

      if (!alive) return;

      if (currentPower.data.length === 0) {
        setDataProcessingStatus('no data');
        return false;
      }

      if (currentPower.status === 200) {
        setDataProcessingStatus('loading');
        setRawPowerData(currentPower.data);
        return true;
      }

      setDataProcessingStatus('no data');
      return false;
    }

    const retrievePowerData = async () => {
      setDataProcessingStatus('loading');

      if (isLiveData) {
        // Live data displays last 6 hours of power data
        const endDate = new Date(); // always use "now"
        const endTimestamp = convertJsToUnixSeconds(endDate);
        const startTimestamp = endTimestamp - 21600; // last 6 hours
        await fetchPowerData(buildingId, startTimestamp, endTimestamp);
      } else {
        // Historical displays power data in selected date range
        const today = new Date();
        const endDate = areDatesOnSameDay(endPicker, today) ? today : endPicker;
        const endTimestamp = convertJsToUnixSeconds(endDate);
        const startTimestamp = convertJsToUnixSeconds(startPicker);
        await fetchPowerData(buildingId, startTimestamp, endTimestamp);
      }

      if (!alive) return;

      setCurrentTime(moment());
    };


    retrievePowerData();

    // Refresh every 30s if isLiveData is true
    let intervalId;
    if (isLiveData) {
      intervalId = setInterval(() => {
        retrievePowerData();
      }, 30000);
    }

    return () => {
      alive = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [buildingId, isLiveData, intervalPicker, startPicker, endPicker, userRole]);

  // Processes data
useEffect(() => {
  let alive = true;

  if (!rawPowerData || rawPowerData.length === 0)
    return { lineChartData: [], lineChartTotal: [] };

  setDataProcessingStatus('loading');

  const groupDataByInterval = (data) => {
    const intervalInSeconds = getIntervalInSeconds(intervalPicker);
    const grouped = {};

    data.forEach(item => {
      const batch = Number(item.batch);
      const keys = Object.keys(item).filter((key) => key !== "batch");

      keys.forEach(key => {
        const value = parseFloat(item[key]);
        if (isNaN(value)) return;

        const intervalBatch = batch - (batch % intervalInSeconds);

        if (!grouped[intervalBatch]) grouped[intervalBatch] = [];
        grouped[intervalBatch].push({ [key]: value });
      })
    });

    return grouped;
  }

  // Helper: Aggregate raw power data by timestamp
  const aggregateRawPowerData = (powerData) => {
    const groupedData = groupDataByInterval(powerData);

    const resultArray = [];
    for (const [timestamp, data] of Object.entries(groupedData)) {
      const uniqueKeys = new Set();
      
      for (const obj of data) {
        for (const key in obj) {
          uniqueKeys.add(key);
        }
      }

      const resultObj = { batch: timestamp };

      for (const key of uniqueKeys) {
        const filteredData = data.filter((obj) => {
          return Object.keys(obj)[0] === key;
        });

        const sumOfTagValues = filteredData.reduce(
          (acc, curr) => acc + curr[key],
          0,
        );

        const avgPower = sumOfTagValues / filteredData.length;
        resultObj[key] = avgPower;
      }

      resultArray.push(resultObj);
    }

    return [...resultArray]; // shallow copy ensures new reference
  };

  // Helper: Transform data for charting
  const transformPowerDataForChart = (powerData) => {
    let total = [];
    const jsTimeFunc = isLiveData ? convertUnixToJs : convertUnixToJsWithDateNoTime;

    const resultArray = powerData.map((obj) => {
      let o = {};
      let valueCount = 0;
      for (let key in obj) {
        if (key === "batch") {
          o["jsTime"] = jsTimeFunc(obj[key]);
        }
        // else if (key === "total") {
        //   total.push({
        //     total: parseFloat(Number(obj[key] / 1000).toFixed(2)),
        //     jsTime: jsTimeFunc(obj.batch),
        //   });
        // }
        else {
          if (key === "total" && valueCount < 2) {
            continue;
          }
          valueCount++;
          o[key.replace("_value", "")] = parseFloat(Number(obj[key] / 1000).toFixed(2));
        }
      }
      return o;
    });

    // Calculate total if no total value in resultArray
    if (
      total.length === 0 &&
      resultArray.length > 0 &&
      Object.keys(resultArray[0]).length > 2 &&
      !resultArray[0].total
    ) {
      total = resultArray.map((obj) => {
        return {
          total: Number(
            Object.values(obj)
              .reduce((acc, curr) => acc + (typeof curr === "number" ? curr : 0), 0)
              .toFixed(2)
          ),
          jsTime: obj.jsTime,
        };
      });
    }

    return [resultArray, total];
  };

  const powerData = isLiveData ? rawPowerData : aggregateRawPowerData(rawPowerData);
  const [resultArray, total] = transformPowerDataForChart(powerData);

  setLineChartData(resultArray);
  setLineChartTotal(total);
  setDataProcessingStatus('loaded data');
}, [intervalPicker, isLiveData, rawPowerData]);


  // ----- End of Line Chart -----
  return (
    <>
      {isLiveData && (
        <div className={styles.FilterHeaderRow}>
          <div className={styles.HeaderTimeFilter}>
            <div>
              Last Updated:{" "}
              <b>{moment(currentTime).format("dddd, MMMM Do YYYY, h:mm:ss a")}</b>
            </div>
          </div>
        </div>
      )}

      <div className={styles.InputRow}>
        <Label>Data Mode:</Label>
        <ButtonGroup>
          <Button
            color={dataMode === 'live' ? 'primary' : 'secondary'}
            onClick={() => setDataMode('live')}
          >
            Live
          </Button>
          <Button
            color={dataMode === 'historical' ? 'primary' : 'secondary'}
            onClick={() => setDataMode('historical')}
          >
            Historical
          </Button>
        </ButtonGroup>
      </div>

      {!isLiveData && (
        <div>
          <div className={styles.InputRow}>
            <Label>Interval:</Label>
            <ButtonGroup>
              {intervals.map((interval) => (
                <Button
                  key={interval}
                  color={intervalPicker === interval ? 'primary' : 'secondary'}
                  onClick={() => setIntervalPicker(interval)}
                >
                  {interval}
                </Button>
              ))}
            </ButtonGroup>
          </div>

          <div className={styles.InputRow}>
            <Label>From</Label>
            <Flatpickr
              id="startDate"
              name="startDate"
              className="col-md-2"
              options={{
                enableTime: false,
                dateFormat: "d-M-Y",
                minDate: moment().subtract(1, "month").startOf("day").toDate(),
                maxDate: endPicker,
              }}
              value={startPicker}
              onChange={([newStartDate]) => {
                setStartPicker(newStartDate);
                const maxEndDate = moment(newStartDate).add(7, "days")
                                    .endOf("day").toDate();
                const today = new Date();
                setEndPicker(maxEndDate > today ? today : maxEndDate);
              }} 
            />
            <Label>To</Label>
            <Flatpickr
              id="endDate"
              name="endDate"
              className="col-md-2"
              options={{
                enableTime: false,
                dateFormat: "d-M-Y",
                minDate: startPicker,
                maxDate: moment.min(
                  moment(startPicker).add(7, "days").endOf("day"),
                  moment().endOf("day")
                ).toDate(),
              }}
              value={endPicker}
              onChange={([newEndDate]) => {
                const today = new Date();
                if (areDatesOnSameDay(newEndDate, today)) {
                  setEndPicker(today);
                } else {
                  setEndPicker(getEndOfDay(newEndDate));
                }
              }}
            />
          </div>
        </div>
      )}

      {(() => {
        switch (dataProcessingStatus) {
          case "loading":
            return <div>Loading data</div>;
          case "no data":
            return <div>No data found</div>;
          case "loaded data":
            return (
              <div>
                {lineChartData.length > 0 && lineChartData.every(d => d.jsTime) && (
                  <div className={styles.LineChartContainer}>
                    <CustomPowerConsumptionChart
                      lineChartData={lineChartData}
                      unit={"kW"}
                      batMSBData={batMSBData}
                      contractCap={contractCap}
                    />
                  </div>
                )}
                {lineChartTotal.length > 0 && lineChartTotal.every(d => d.jsTime) && (
                  <div className={styles.LineChartContainer}>
                    <CustomPowerConsumptionChart
                      lineChartData={lineChartTotal}
                      unit={"kW"}
                      batMSBData={batMSBData}
                      contractCap={contractCap}
                    />
                  </div>
                )}
              </div>
            );
          default:
            return null;
        }
      })()}
    </>
  );
}

export default PowerConsumptionChart;
